import React, { forwardRef } from 'react';
import { cn } from '@/utils/cn';

export interface IInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
    /**
     * The visual style variant of the input
     */
    variant?: 'default' | 'error' | 'success';

    /**
     * The size of the input
     */
    size?: 'sm' | 'md' | 'lg';

    /**
     * Whether the input has full width
     */
    fullWidth?: boolean;

    /**
     * Error message to display below the input
     */
    error?: string;

    /**
     * Help text to display below the input
     */
    helpText?: string;

    /**
     * Icon to display on the left side
     */
    leftIcon?: React.ReactNode;

    /**
     * Icon to display on the right side
     */
    rightIcon?: React.ReactNode;
}

/**
 * Shared Input component following BiBi design system standards
 * 
 * Consolidates 16+ duplicate input field implementations across forms
 * Provides consistent styling, validation, and accessibility
 * 
 * @example
 * ```tsx
 * <Input
 *   label="Email Address"
 *   type="email"
 *   placeholder="Enter your email"
 *   error={errors.email?.message}
 *   {...register('email')}
 * />
 * ```
 */
export const Input = forwardRef<HTMLInputElement, IInputProps>(
    (
        {
            variant = 'default',
            size = 'md',
            fullWidth = true,
            error,
            helpText,
            leftIcon,
            rightIcon,
            className,
            disabled,
            ...props
        },
        ref
    ) => {
        // Determine focus ring color based on variant
        const focusRingColor = error
            ? 'focus:ring-red-500 focus:border-red-500'
            : 'focus:ring-bibi-blue focus:border-bibi-blue';

        // Base input classes following BiBi design system
        const baseInputClasses = cn(
            // Layout
            'border rounded-lg transition-colors outline-none',

            // Typography
            'font-medium placeholder:text-gray-500',

            // States
            'hover:border-gray-400',
            'disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed',
            'disabled:border-gray-200',

            // Focus states
            'focus:ring-2 focus:border-transparent',
            focusRingColor,

            // Variant styles
            {
                // Default variant
                'border-gray-300 bg-white text-gray-900': variant === 'default' && !error,

                // Error variant
                'border-red-300 bg-red-50 text-red-900 placeholder:text-red-400': variant === 'error' || !!error,

                // Success variant  
                'border-green-300 bg-green-50 text-green-900': variant === 'success',
            },

            // Size variants
            {
                'px-3 py-2 text-sm': size === 'sm',
                'px-4 py-3 text-base': size === 'md',
                'px-5 py-4 text-lg': size === 'lg',
            },

            // Width
            {
                'w-full': fullWidth,
            },

            // Icon padding adjustments
            {
                'pl-10': !!leftIcon && size === 'md',
                'pl-12': !!leftIcon && size === 'lg',
                'pl-8': !!leftIcon && size === 'sm',
                'pr-10': !!rightIcon && size === 'md',
                'pr-12': !!rightIcon && size === 'lg',
                'pr-8': !!rightIcon && size === 'sm',
            },

            className
        );

        return (
            <div className={cn('relative', { 'w-full': fullWidth })}>
                {/* Left Icon */}
                {leftIcon && (
                    <div className={cn(
                        'absolute left-0 top-0 h-full flex items-center justify-center text-gray-400',
                        {
                            'w-8': size === 'sm',
                            'w-10': size === 'md',
                            'w-12': size === 'lg',
                        }
                    )}>
                        {leftIcon}
                    </div>
                )}

                {/* Input Field */}
                <input
                    ref={ref}
                    className={baseInputClasses}
                    disabled={disabled}
                    aria-invalid={error ? 'true' : 'false'}
                    aria-describedby={
                        error ? `${props.id}-error` :
                            helpText ? `${props.id}-help` : undefined
                    }
                    {...props}
                />

                {/* Right Icon */}
                {rightIcon && (
                    <div className={cn(
                        'absolute right-0 top-0 h-full flex items-center justify-center text-gray-400',
                        {
                            'w-8': size === 'sm',
                            'w-10': size === 'md',
                            'w-12': size === 'lg',
                        }
                    )}>
                        {rightIcon}
                    </div>
                )}

                {/* Error Message */}
                {error && (
                    <p
                        id={`${props.id}-error`}
                        className="mt-2 text-sm text-red-600"
                        role="alert"
                    >
                        {error}
                    </p>
                )}

                {/* Help Text */}
                {helpText && !error && (
                    <p
                        id={`${props.id}-help`}
                        className="mt-2 text-sm text-gray-500"
                    >
                        {helpText}
                    </p>
                )}
            </div>
        );
    }
);

Input.displayName = 'Input';
